<!-- eslint-disable max-len -->
<template>
  <div class="privacy-page">

    <!-- Banner -->
    <div class="privacy-banner">
      <div class="privacy-banner__inner">
        <p class="privacy-banner__eyebrow">Vive Konecta</p>
        <h1 class="privacy-banner__title">Solicitud de No Contactabilidad</h1>
        <p class="privacy-banner__date">Ejerce tu derecho a no ser contactado</p>
      </div>
    </div>

    <!-- Content -->
    <div class="privacy-content">

      <p class="privacy-lead">
        En <strong>Vive Konecta</strong> respetamos tu privacidad y tu derecho a decidir si deseas recibir
        comunicaciones de nuestra parte. Al completar este formulario, tu número de celular, correo electrónico
        y DNI quedarán registrados en nuestra <strong>lista de no contactabilidad</strong>, lo que significa que
        no volverás a ser contactado para procesos de selección, ofertas laborales u otras comunicaciones
        de reclutamiento.
      </p>
      <p class="privacy-lead">
        Este registro es <strong>permanente e inmediato</strong>: una vez procesada tu solicitud, tus datos
        serán incluidos en nuestra lista de exclusión y ningún asesor ni canal digital de Vive Konecta
        podrá volverte a contactar, conforme a la
        <strong>Ley N° 29733 – Ley de Protección de Datos Personales</strong> y su Reglamento.
      </p>

      <div class="privacy-divider"></div>

      <!-- Aviso -->
      <div class="privacy-section">
        <div class="nc-notice">
          <p>
            Todos los campos son obligatorios. Asegúrate de ingresar los datos exactos con los que fuiste
            registrado para que podamos identificarte correctamente y añadirte a la lista de no contactabilidad.
          </p>
        </div>
      </div>

      <!-- Formulario -->
      <div class="privacy-section">

        <!-- Estado: enviado con éxito -->
        <div v-if="submitted" class="nc-success">
          <h2 class="nc-success__title">Solicitud enviada</h2>
          <p>
            Hemos recibido tu solicitud de no contactabilidad. Tus datos han sido incluidos en nuestra
            lista de exclusión y no volverás a ser contactado por Vive Konecta.
          </p>
          <button class="nc-btn nc-btn--outline" @click="resetForm">Enviar otra solicitud</button>
        </div>

        <!-- Formulario activo -->
        <v-form v-else ref="formNc" v-model="isValid" :lazy-validation="false" @submit.prevent="handleSubmit">

          <!-- Honeypot -->
          <input
            v-model="honeypot"
            type="text"
            name="website"
            autocomplete="off"
            tabindex="-1"
            aria-hidden="true"
            class="nc-honeypot"
          />

          <!-- Nombre -->
          <p class="nc-label">Nombre completo <span class="nc-required">*</span></p>
          <v-text-field
            v-model="form.nombre"
            label="Ingresa tu nombre completo"
            outlined
            color="#1300d4"
            :rules="nombreRules"
            type="text"
            maxlength="120"
            autocomplete="name"
            required
          />

          <!-- Correo electrónico -->
          <p class="nc-label">Correo electrónico <span class="nc-required">*</span></p>
          <v-text-field
            v-model="form.correo"
            label="correo@ejemplo.com"
            outlined
            color="#1300d4"
            :rules="correoRules"
            type="email"
            maxlength="120"
            autocomplete="email"
            required
          />

          <!-- DNI -->
          <p class="nc-label">Número de DNI <span class="nc-required">*</span></p>
          <v-text-field
            v-model="form.dni"
            label="Ingresa tu DNI (8 dígitos)"
            outlined
            color="#1300d4"
            :rules="dniRules"
            type="text"
            inputmode="numeric"
            maxlength="8"
            pattern="[0-9]*"
            autocomplete="off"
            oninput="this.value = this.value.replace(/[^0-9]/g, '').slice(0, 8)"
            oncopy="return false"
            onpaste="return false"
            @keypress="onlyNumbers"
            required
          />

          <!-- Celular -->
          <p class="nc-label">Número de celular <span class="nc-required">*</span></p>
          <v-text-field
            v-model="form.celular"
            label="Ingresa tu celular (9 dígitos)"
            outlined
            color="#1300d4"
            :rules="celularRules"
            type="text"
            inputmode="numeric"
            maxlength="9"
            pattern="[0-9]*"
            autocomplete="tel"
            oninput="this.value = this.value.replace(/[^0-9]/g, '').slice(0, 9)"
            oncopy="return false"
            onpaste="return false"
            @keypress="onlyNumbers"
            required
          />

          <!-- Intentos agotados -->
          <div v-if="blocked" class="nc-error-box">
            Has superado el número máximo de intentos. Por favor, espera unos minutos antes de intentarlo nuevamente.
          </div>

          <!-- Error de envío -->
          <div v-if="submitError" class="nc-error-box">
            {{ submitError }}
          </div>

          <!-- Nota reCAPTCHA -->
          <p class="nc-captcha-note">
            Este formulario está protegido por reCAPTCHA v3. Al enviarlo aceptas las
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" class="privacy-link">Políticas de privacidad</a>
            y los <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" class="privacy-link">Términos de servicio</a>
            de Google.
          </p>

          <!-- Botón enviar -->
          <button
            type="submit"
            class="nc-btn nc-btn--primary"
            :disabled="isLoading || blocked"
          >
            <span v-if="isLoading" class="nc-btn__spinner"></span>
            <span v-else>Enviar solicitud</span>
          </button>

        </v-form>
      </div>

      <div class="privacy-divider"></div>

      <div class="privacy-footer-nav">
        <span class="privacy-footer-nav__back" @click="$router.push('/')">&larr; Volver al inicio</span>
        <router-link to="/politica-de-privacidad" class="privacy-footer-nav__link">Política de privacidad &rarr;</router-link>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { loginLanding } from '@/services/dynamo/postulants';

const MAX_ATTEMPTS = 3;
const MIN_FILL_MS = 3000;

export default {
  name: 'NoContactabilidadPage',

  data() {
    return {
      isValid: false,
      isLoading: false,
      submitted: false,
      blocked: false,
      submitError: null,
      honeypot: '',
      formStartTime: null,
      attempts: 0,

      form: {
        nombre: '',
        correo: '',
        dni: '',
        celular: '',
      },

      nombreRules: [
        (v) => !!v || 'El nombre es requerido',
        (v) => v.trim().length >= 2 || 'El nombre debe tener al menos 2 caracteres',
      ],

      correoRules: [
        (v) => !!v || 'El correo es requerido',
        (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Ingresa un correo válido',
      ],

      dniRules: [
        (v) => !!v || 'El DNI es requerido',
        (v) => /^\d{8}$/.test(v) || 'El DNI debe tener exactamente 8 dígitos',
      ],

      celularRules: [
        (v) => !!v || 'El celular es requerido',
        (v) => /^9\d{8}$/.test(v) || 'El celular debe tener 9 dígitos y comenzar con 9',
      ],
    };
  },

  created() {
    this.formStartTime = Date.now();

    const stored = sessionStorage.getItem('nc_attempts');
    if (stored) {
      this.attempts = parseInt(stored, 10);
      if (this.attempts >= MAX_ATTEMPTS) this.blocked = true;
    }

    if (this.$gtag) {
      this.$gtag.pageview({
        page_title: 'No Contactabilidad',
        page_path: '/no-contactabilidad',
      });
    }
  },

  methods: {
    onlyNumbers(event) {
      if (!/[0-9]/.test(String.fromCharCode(event.which))) event.preventDefault();
    },

    resetForm() {
      this.submitted = false;
      this.submitError = null;
      this.form = { nombre: '', correo: '', dni: '', celular: '' };
      this.honeypot = '';
      this.formStartTime = Date.now();
      this.$refs.formNc && this.$refs.formNc.reset();
    },

    async handleSubmit() {
      if (this.honeypot) return;
      if (this.blocked) return;

      if (Date.now() - this.formStartTime < MIN_FILL_MS) {
        this.submitError = 'El formulario se completó demasiado rápido. Por favor, revisa tus datos e intenta nuevamente.';
        return;
      }

      if (!this.$refs.formNc.validate()) return;

      this.isLoading = true;
      this.submitError = null;

      try {
        await this.$recaptchaLoaded();
        const gcaptcha = await this.$recaptcha('no_contactabilidad');

        const token = await loginLanding();
        await axios.post(
          `${process.env.VUE_APP_HOST_NEW_VK}/postulations/no-contact`,
          {
            name: this.form.nombre.trim(),
            email: this.form.correo.trim().toLowerCase(),
            documentNumber: this.form.dni.trim(),
            phone: this.form.celular.trim(),
            gcaptcha,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        );

        this.submitted = true;
        this.attempts = 0;
        sessionStorage.removeItem('nc_attempts');

        this.$gtag?.event('NC_submit_success', {
          event_category: 'no_contactabilidad',
          event_label: 'NC_submit_success',
          value: 1,
        });
      } catch (error) {
        this.attempts += 1;
        sessionStorage.setItem('nc_attempts', this.attempts);

        if (this.attempts >= MAX_ATTEMPTS) {
          this.blocked = true;
          this.submitError = null;
        } else {
          const status = error?.response?.status;
          if (status === 429) {
            this.submitError = 'Has realizado demasiadas solicitudes. Por favor, espera unos minutos.';
          } else if (status === 400) {
            this.submitError = 'Los datos ingresados no son válidos. Verifica e intenta nuevamente.';
          } else {
            this.submitError = 'Ocurrió un error al enviar tu solicitud. Por favor, intenta nuevamente.';
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
$blue:      #1300d4;
$dark-blue: #0f0f72;
$white:     #ffffff;
$text:      #1a1a2e;
$text-light: #555;
$border:    #e4e4ee;
$bg-subtle: #f7f7fb;
$red:       #d32f2f;
$green:     #1b7a4a;
$font:      'Poppins', 'Barlow', sans-serif;

/* ── Heredados del patrón privacy ── */
.privacy-page {
  font-family: $font;
  color: $text;
  background: $white;
}

.privacy-banner {
  background-color: $blue;
  padding: 100px 24px 88px;
  min-height: 260px;
  display: flex;
  align-items: center;
}

.privacy-banner__inner {
  max-width: 820px;
  margin: 0 auto;
  width: 100%;
}

.privacy-banner__eyebrow {
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  margin: 0 0 10px;
}

.privacy-banner__title {
  font-size: clamp(2rem, 5vw, 3.25rem);
  font-weight: 700;
  color: $white;
  margin: 0 0 16px;
  line-height: 1.15;
}

.privacy-banner__date {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  display: inline-block;
  background: rgba(255, 255, 255, 0.12);
  padding: 4px 14px;
  border-radius: 20px;
}

.privacy-content {
  max-width: 820px;
  margin: 0 auto;
  padding: 60px 24px 80px;

  p {
    font-size: 1rem;
    line-height: 1.8;
    color: $text;
    margin: 0 0 14px;
  }

  strong {
    color: $dark-blue;
    font-weight: 600;
  }
}

.privacy-lead {
  font-size: 1.05rem !important;
  line-height: 1.85 !important;
  color: #333 !important;
}

.privacy-divider {
  border: none;
  border-top: 2px solid $border;
  margin: 40px 0;
}

.privacy-section {
  margin-bottom: 44px;

  p { margin: 0 0 12px; }
}

.privacy-link {
  color: $blue;
  font-weight: 600;
  text-decoration: underline;

  &:hover { color: $dark-blue; }
}

.privacy-footer-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 8px;

  &__back {
    cursor: pointer;
    font-size: 0.95rem;
    font-weight: 600;
    color: $blue;
    &:hover { color: $dark-blue; }
  }

  &__link {
    font-size: 0.95rem;
    font-weight: 600;
    color: $blue;
    text-decoration: none;
    &:hover { color: $dark-blue; }
  }
}

@media (max-width: 600px) {
  .privacy-banner { padding: 48px 16px 40px; }
}

/* ── Específicos del formulario ── */
.nc-notice {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: $bg-subtle;
  border-left: 4px solid $blue;
  padding: 16px 20px;
  border-radius: 0 8px 8px 0;

  p { margin: 0; font-size: 0.95rem; color: $text-light; }
}

.nc-notice__icon {
  font-size: 1.3rem;
  color: $blue;
  flex-shrink: 0;
  margin-top: 2px;
}

.nc-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: $text;
  margin: 0 0 6px;
}

.nc-required { color: $red; }

.nc-honeypot {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.nc-error-box {
  background: #fff3f3;
  border: 1px solid #f5c6c6;
  border-radius: 8px;
  padding: 12px 16px;
  color: $red;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 16px;
}

.nc-captcha-note {
  font-size: 0.78rem;
  color: #888;
  line-height: 1.5;
  margin: 0 0 20px;
}

.nc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: $font;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
  padding: 14px 32px;
  width: 100%;

  &--primary {
    background: $blue;
    color: $white;
    &:hover:not(:disabled) { background: $dark-blue; }
    &:disabled { opacity: 0.55; cursor: not-allowed; }
  }

  &--outline {
    background: transparent;
    color: $blue;
    border: 2px solid $blue;
    margin-top: 8px;
    &:hover { background: rgba($blue, 0.06); }
  }
}

.nc-btn__spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: $white;
  border-radius: 50%;
  animation: nc-spin 0.7s linear infinite;
}

@keyframes nc-spin {
  to { transform: rotate(360deg); }
}

.nc-success {
  text-align: center;
  padding: 20px 0 8px;
}

.nc-success__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: #e6f4ee;
  color: $green;
  font-size: 2rem;
  border-radius: 50%;
  margin-bottom: 20px;
}

.nc-success__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: $green;
  margin-bottom: 12px;
}
</style>
